import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type WipEntryType =
  | 'time'
  | 'write_up'
  | 'write_down'
  | 'invoice'
  | 'reversal'
  | 'credit_note'
  | 'write_off'
  | 'discount'
  | 'advance';

export interface WipEntry {
  id: string;
  tenantId: string;
  jobId: string;
  jobTitle: string;
  clientName: string;
  entryType: WipEntryType;
  description: string;
  hours: number | null;
  rate: number | null;
  amount: number;
  runningBalance: number;
  staffName: string | null;
  createdAt: string;
}

export interface WipJobSummary {
  jobId: string;
  jobTitle: string;
  clientName: string;
  totalHours: number;
  wipBalance: number;
  invoicedAmount: number;
  writeUpTotal: number;
  writeDownTotal: number;
  realizationRate: number;
  entries: WipEntry[];
}

export interface RealizationData {
  totalWipValue: number;
  totalInvoicedValue: number;
  realizationRate: number;
  byPeriod: RealizationPeriod[];
}

export interface RealizationPeriod {
  jobId: string;
  jobTitle: string;
  clientName: string;
  wipValue: number;
  invoicedValue: number;
  realizationRate: number;
}

export interface StaffProfitability {
  userId: string;
  staffName: string;
  totalHours: number;
  billableHours: number;
  costRate: number;
  totalCost: number;
  billedRevenue: number;
  profit: number;
  profitMargin: number;
  utilizationRate: number;
}

export interface ClientProfitability {
  clientId: string;
  clientName: string;
  totalHours: number;
  totalCost: number;
  billedRevenue: number;
  writeUps: number;
  writeDowns: number;
  netRevenue: number;
  profit: number;
  profitMargin: number;
}

export interface UtilizationData {
  userId: string;
  staffName: string;
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  utilizationRate: number;
}

// ─── Request Types ──────────────────────────────────────────────────────────

export interface WipLedgerParams {
  page?: number;
  pageSize?: number;
  jobId?: string;
  teamId?: string;
  dateFrom?: string;
  dateTo?: string;
  entryType?: WipEntryType | '';
}

export interface WipLedgerResponse {
  entries: WipEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalWipBalance: number;
}

export interface WriteUpPayload {
  jobId: string;
  amount: number;
  description: string;
}

export interface WriteDownPayload {
  jobId: string;
  amount: number;
  description: string;
}

export interface ProfitabilityParams {
  teamId?: string;
  dateFrom?: string;
  dateTo?: string;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const wipKeys = {
  all: ['wip'] as const,
  ledger: () => [...wipKeys.all, 'ledger'] as const,
  ledgerList: (params: WipLedgerParams) => [...wipKeys.ledger(), params] as const,
  jobDetail: (jobId: string) => [...wipKeys.all, 'job', jobId] as const,
  realization: () => [...wipKeys.all, 'realization'] as const,
  profitabilityStaff: (params: ProfitabilityParams) =>
    [...wipKeys.all, 'profitability', 'staff', params] as const,
  profitabilityClient: (params: ProfitabilityParams) =>
    [...wipKeys.all, 'profitability', 'client', params] as const,
  utilization: (params: ProfitabilityParams) => [...wipKeys.all, 'utilization', params] as const,
};

// ─── Ledger Hooks ───────────────────────────────────────────────────────────

/**
 * Fetch paginated WIP ledger entries with filters.
 * Requirements: 17.1
 */
export function useWipLedger(params: WipLedgerParams = {}) {
  return useQuery({
    queryKey: wipKeys.ledgerList(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<WipLedgerResponse>>('/wip/ledger', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 25,
          job_id: params.jobId || undefined,
          team_id: params.teamId || undefined,
          date_from: params.dateFrom || undefined,
          date_to: params.dateTo || undefined,
          entry_type: params.entryType || undefined,
        },
      });
      return response.data.data;
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * Fetch WIP detail for a specific job.
 * Requirements: 17.1
 */
export function useWipJobDetail(jobId: string) {
  return useQuery({
    queryKey: wipKeys.jobDetail(jobId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<WipJobSummary>>(`/wip/jobs/${jobId}`);
      return response.data.data;
    },
    enabled: !!jobId,
  });
}

// ─── Write-Up / Write-Down Hooks ────────────────────────────────────────────

/**
 * Submit a write-up adjustment to WIP value.
 * Requirements: 17.3
 */
export function useWriteUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: WriteUpPayload) => {
      const response = await apiClient.post<ApiResponse<WipEntry>>('/wip/write-up', {
        job_id: payload.jobId,
        amount: payload.amount,
        description: payload.description,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: wipKeys.all });
    },
  });
}

/**
 * Submit a write-down adjustment to WIP value.
 * Requirements: 17.3
 */
export function useWriteDown() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: WriteDownPayload) => {
      const response = await apiClient.post<ApiResponse<WipEntry>>('/wip/write-down', {
        job_id: payload.jobId,
        amount: payload.amount,
        description: payload.description,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: wipKeys.all });
    },
  });
}

// ─── Realization Hook ───────────────────────────────────────────────────────

/**
 * Fetch realization metrics (invoiced vs WIP balance).
 * Requirements: 17.2
 */
export function useRealization(params: ProfitabilityParams = {}) {
  return useQuery({
    queryKey: [...wipKeys.realization(), params],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<RealizationData>>('/wip/realization', {
        params: {
          team_id: params.teamId || undefined,
          date_from: params.dateFrom || undefined,
          date_to: params.dateTo || undefined,
        },
      });
      return response.data.data;
    },
  });
}

// ─── Profitability Hooks ────────────────────────────────────────────────────

/**
 * Fetch staff-level profitability report.
 * Requirements: 17.4
 */
export function useStaffProfitability(params: ProfitabilityParams = {}) {
  return useQuery({
    queryKey: wipKeys.profitabilityStaff(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<StaffProfitability[]>>(
        '/wip/profitability/staff',
        {
          params: {
            team_id: params.teamId || undefined,
            date_from: params.dateFrom || undefined,
            date_to: params.dateTo || undefined,
          },
        },
      );
      return response.data.data;
    },
  });
}

/**
 * Fetch client-level profitability report.
 * Requirements: 17.6
 */
export function useClientProfitability(params: ProfitabilityParams = {}) {
  return useQuery({
    queryKey: wipKeys.profitabilityClient(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<ClientProfitability[]>>(
        '/wip/profitability/client',
        {
          params: {
            team_id: params.teamId || undefined,
            date_from: params.dateFrom || undefined,
            date_to: params.dateTo || undefined,
          },
        },
      );
      return response.data.data;
    },
  });
}

/**
 * Fetch staff utilization data.
 * Requirements: 17.5
 */
export function useUtilization(params: ProfitabilityParams = {}) {
  return useQuery({
    queryKey: wipKeys.utilization(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<UtilizationData[]>>('/wip/utilization', {
        params: {
          team_id: params.teamId || undefined,
          date_from: params.dateFrom || undefined,
          date_to: params.dateTo || undefined,
        },
      });
      return response.data.data;
    },
  });
}
